/**
 * 登录页
 * 门店前台每天早上要登一次，所以只要三个输入框，且商户编码会自动记住。
 */
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FormField, FormStack, TextInput } from '@/components/ui/form';
import { NoteBox } from '@/components/ui/note-box';
import { ApiError } from '@/lib/api/http';
import { useAuth } from '@/lib/auth/use-auth';
import { readSession } from '@/lib/auth/session-storage';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | null)?.message;

  // 商户编码对门店来说是"我们店的编号"，每次都让人重输很烦，这里预填上次登录用过的
  const [merchantCode, setMerchantCode] = useState(
    () => readSession()?.merchantCode ?? '',
  );
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ merchantCode: merchantCode.trim(), mobile, password });
      // 跳到根路径由 HomeRedirect 按权限决定进老板端还是员工端。
      // 不能在这里直接算：login() 刚返回时，本组件闭包里的 can() 还是登录前那一份，
      // 拿它判断会把发型师送进老板端。
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : '登录失败，请稍后重试',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-paper px-6 py-12">
      <div className="w-full max-w-[420px]">
        <div className="mb-7 flex items-center gap-3">
          <span className="grid size-12 place-items-center rounded-[15px] bg-linear-145 from-brand-3 to-brand-2 text-[22px] font-bold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,.25)] [font-family:Georgia,serif]">
            T
          </span>
          <div>
            <h1 className="text-page-title font-extrabold tracking-title">
              TONI 掌店
            </h1>
            <p className="text-note text-ink-3">把门店管得明明白白</p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-line bg-panel p-6 shadow-e2"
        >
          <FormStack>
            <FormField label="商户编码" required>
              <TextInput
                value={merchantCode}
                onChange={(event) => setMerchantCode(event.target.value)}
                placeholder="例如 TONI-DEMO"
                autoComplete="organization"
                required
              />
            </FormField>

            <FormField label="手机号" required>
              <TextInput
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                placeholder="登录手机号"
                autoComplete="username"
                inputMode="numeric"
                required
              />
            </FormField>

            {/*
              **提示的位数必须和服务端 LoginDto 的下限一模一样。**

              这里写着 6、服务端收 8 的时候，用户输 7 位会看到
              "密码至少 6 个字符"——照着这句话反复试，永远试不出来，
              因为真实规则是 8。数字对不上比不写数字更糟。
            */}
            <FormField label="密码" required hint="至少 8 位">
              <TextInput
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="登录密码"
                autoComplete="current-password"
                required
              />
            </FormField>

            {error && <NoteBox tone="danger">{error}</NoteBox>}
            {successMessage && !error && <NoteBox tone="ok">{successMessage}</NoteBox>}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              loading={submitting}
            >
              登录
            </Button>
          </FormStack>
        </form>

        <p className="mt-4 text-center text-tiny text-ink-3">
          忘记密码请联系为你开通系统的服务人员重置
        </p>
      </div>
    </div>
  );
}
